"use client";

import { Instagram, Mail, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Leadership() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const curators = [{
    name: "Karim El Khadrawy",
    role: "Founder & CEO",
    bio: "With over 15 years of experience in mobile phone trading and B2B sales across the MENA region and beyond, Karim founded KTEX FZCO to bring structure and discipline to wholesale trading. Operating from DAFZA, Dubai, he has built a reputation for closing high-value deals, managing international supplier relationships, and expanding into 40+ global markets. His approach combines deep market knowledge with rigorous risk assessment — ensuring every partnership delivers measurable, long-term results.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/5D900E3E-1AEB-4803-A47D-9834D04B24C9.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/5D900E3E-1AEB-4803-A47D-9834D04B24C9.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/5D900E3E-1AEB-4803-A47D-9834D04B24C9.jpg-wvc-sizes",
    specialties: "TRADE · STRATEGY · EXPANSION",
    instagram: "https://instagram.com",
    linkedin: "https://www.linkedin.com/in/karim-elkhadrawy",
    email: "karim.elkhadrawy@gmail.com"
  }];
  return <section data-section-id="5001" id="leadership-team" className="relative bg-[var(--color-background)] text-[var(--color-foreground)] py-24 md:py-36 lg:py-44 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20 lg:mb-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs tracking-[0.25em] text-[var(--color-primary-500)] mb-8 uppercase">
              I — Our Leadership
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-[var(--color-foreground)] leading-[1.05] tracking-tight">
              The vision
              <br />
              <span className="italic text-[var(--color-primary-400)]">driving our future.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:pt-6">
            <p className="font-default text-base md:text-[17px] text-[var(--color-primary-700)] leading-relaxed">
              Guided by decades of global commerce expertise, our leadership is committed to innovation, 
              integrity, and strategic excellence across every international venture we undertake.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {curators.map((curator, i) => <CuratorCard key={curator.name} data-index={i} curator={curator} offset={false} />)}
        </div>
      </div>
    </section>;
}
type CuratorCardProps = {
  curator: {
    name: string;
    role: string;
    bio: string;
    image: string;
    srcset: string;
    sizes: string;
    specialties: string;
    instagram: string;
    linkedin?: string;
    email: string;
  };
  offset: boolean;
  "data-index": number;
};
function CuratorCard({
  curator,
  offset,
  ...props
}: CuratorCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={props["data-index"]} className={`group relative transition-all duration-1000 ease-out ${offset ? "md:mt-20" : ""} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="relative flex flex-col md:flex-row items-start gap-8 lg:gap-12">
        <div className="flex gap-4 md:gap-6 shrink-0">
          <div className="hidden md:flex shrink-0 items-start pt-4">
            <p className="font-default text-[11px] tracking-[0.2em] text-[var(--color-primary-400)] uppercase whitespace-nowrap" style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)"
          }}>
              {curator.specialties}
            </p>
          </div>

          <div className="relative w-full sm:w-[320px] aspect-[320/440] overflow-hidden rounded-lg shadow-md">
            <img src={curator.image} data-wvc-srcset={curator.srcset} data-wvc-sizes={curator.sizes} alt={`Portrait of ${curator.name}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.02]" />
            <div className="absolute inset-0 transition-opacity duration-700 ease-out opacity-0 group-hover:opacity-100" aria-hidden="true">
              <img src={curator.image} data-wvc-srcset={curator.srcset} data-wvc-sizes={curator.sizes} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 md:pt-4">
          <p className="md:hidden font-default text-[11px] tracking-[0.2em] text-[var(--color-primary-400)] uppercase mb-3">
            {curator.specialties}
          </p>

          <h3 className="font-default text-3xl md:text-4xl text-[var(--color-foreground)] leading-tight mb-2">
            {curator.name}
          </h3>

          <p className="font-default italic text-base text-[var(--color-primary-500)] mb-6">
            {curator.role}
          </p>

          <p className="font-default text-base md:text-lg text-[var(--color-primary-700)] leading-relaxed mb-8 max-w-xl">
            {curator.bio}
          </p>

          <div className="flex items-center gap-4 pt-4 border-t border-[var(--color-primary-200)] max-w-xl">
            <Link to={curator.instagram} newTab aria-label={`${curator.name} on Instagram`} className="text-[var(--color-primary-500)] hover:text-[var(--color-accent-500)] transition-colors duration-300 py-2">
              <Instagram className="w-5 h-5" strokeWidth={1.25} />
            </Link>
            {curator.linkedin && <>
                <span className="w-px h-3 bg-[var(--color-primary-200)]" aria-hidden="true" />
                <Link to={curator.linkedin} newTab aria-label={`${curator.name} on LinkedIn`} className="text-[var(--color-primary-500)] hover:text-[var(--color-accent-500)] transition-colors duration-300 py-2">
                  <Linkedin className="w-5 h-5" strokeWidth={1.25} />
                </Link>
              </>}
            <span className="w-px h-3 bg-[var(--color-primary-200)]" aria-hidden="true" />
            <Link to={`mailto:${curator.email}`} aria-label={`Email ${curator.name}`} className="text-[var(--color-primary-500)] hover:text-[var(--color-accent-500)] transition-colors duration-300 py-2">
              <Mail className="w-5 h-5" strokeWidth={1.25} />
            </Link>
          </div>
        </div>
      </div>
    </div>;
}